import React, { useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FiSend, FiMessageSquare } from "react-icons/fi";
import { Avatar, Button, Card, EmptyState, Spinner } from "../ui";
import { getCommunityMessages, sendCommunityMessage } from "../../api/communityChats";
import { NotificationContext } from "../../context/NotificationContext";
import { useToast } from "../../context/ToastContext";
import useUsersMap from "../../hooks/useUsersMap";
import { formatSmartTimestamp } from "../../utils/format";

export default function ChatTab({ communityId, currentUser, isJoined }) {
  const { subscribeToCommunityMessages } = useContext(NotificationContext);
  const { notify } = useToast();
  const { usersById } = useUsersMap();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    getCommunityMessages(communityId)
      .then((res) => setMessages(res.data?.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [communityId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Live chat updates — a new message from someone else appears instantly
  // instead of requiring the tab to be left and reopened (or a full page
  // refresh) to see it.
  useEffect(() => {
    const unsubscribe = subscribeToCommunityMessages((incoming) => {
      if (String(incoming.communityId) !== String(communityId)) return;
      setMessages((prev) => (prev.some((m) => m.id === incoming.id) ? prev : [...prev, incoming]));
    });
    return unsubscribe;
  }, [communityId, subscribeToCommunityMessages]);

  const handleSend = async () => {
    if (!draft.trim()) return;
    const text = draft.trim();
    setDraft("");
    setSending(true);
    const optimistic = {
      id: `temp-${Date.now()}`,
      content: text,
      senderId: currentUser?.id,
      sender: currentUser,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    try {
      await sendCommunityMessage(communityId, text);
    } catch {
      notify("Message failed.", "error");
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="flex flex-col" style={{ height: "520px" }}>
      <div className="flex-1 overflow-y-auto space-y-3 p-4 scrollbar-thin">
        {loading ? (
          <div className="flex h-full items-center justify-center">
            <Spinner />
          </div>
        ) : messages.length === 0 ? (
          <EmptyState className="border-none" icon={<FiMessageSquare className="h-6 w-6" />} title="No messages yet" description="Start the conversation." />
        ) : (
          messages.map((m) => {
            const senderId = m.senderId;
            const sender = usersById[senderId] || m.sender;
            const mine = senderId === currentUser?.id;
            const name = sender?.name ? `${sender.name}${sender.lastName ? " " + sender.lastName : ""}` : "Member";
            return (
              <div key={m.id} className={`flex items-end gap-2 ${mine ? "flex-row-reverse" : ""}`}>
                <Link to={`/profile/${senderId}`}>
                  <Avatar name={name} size="xs" />
                </Link>
                <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${mine ? "bg-primary-600 text-white" : "bg-slate-100 text-slate-700"}`}>
                  {!mine && <p className="mb-0.5 text-[11px] font-semibold text-primary-600">{name}</p>}
                  <p className="whitespace-pre-wrap">{m.content}</p>
                  <p className={`mt-1 text-[10px] ${mine ? "text-primary-200" : "text-slate-400"}`}>{formatSmartTimestamp(m.createdAt)}</p>
                </div>
              </div>
            );
          })
        )}
        <div ref={chatEndRef} />
      </div>
      <div className="flex items-center gap-2 border-t border-slate-100 p-3">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), handleSend())}
          placeholder={isJoined ? "Write a message…" : "Join this community to chat"}
          disabled={!isJoined}
          className="h-10 flex-1 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100 disabled:bg-slate-50"
        />
        <Button size="sm" onClick={handleSend} loading={sending} disabled={!isJoined || !draft.trim()}>
          <FiSend className="h-4 w-4" />
        </Button>
      </div>
    </Card>
  );
}
